import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUser,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaLanguage,
  FaUserTag,
  FaClock,
  FaMoneyBillWave,
  FaInfoCircle,
  FaCheckCircle,
  FaStethoscope,
} from "react-icons/fa";
import config, { getUserCity, fetchRemoteConfig } from "../../../config";
import TermsAcceptStep from "./TermsAcceptStep";

const BASE_URL = config.API_BASE_URL || "http://localhost:8000";

export const NURSING_OPTIONS = [
  { value: "bp-glucose-monitoring",   label: "BP & Glucose Monitoring"              },
  { value: "wound-dressing",          label: "Wound Care & Dressing Change"          },
  { value: "catheter-maintenance",    label: "Catheter & Colostomy Bag Maintenance"  },
  { value: "iv-monitoring",           label: "IV Line Monitoring (no new insertion)" },
  { value: "vitals-documentation",    label: "Vitals Documentation & Reporting"      },
  { value: "injection-support",       label: "Injection Assistance (pre-filled)"     },
  { value: "nasogastric-tube-care",   label: "Nasogastric Tube Feeding Care"         },
  { value: "oxygen-monitoring",       label: "Oxygen Level & SpO2 Monitoring"        },
  { value: "physiotherapy-assist",    label: "Physiotherapy Exercise Assistance"     },
];

const HomeNursingSupport = () => {
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [submitError,  setSubmitError]  = useState("");
  const [step, setStep] = useState("form"); // "form" -> "terms"

  const categoryKey  = "care-services";
  const serviceKey   = "home-nursing";
  // City/Area come from the admin-configured backend list (config_data
  // collection), not a hardcoded array — see the same pattern's comment
  // in ElderCare.js for why this re-syncs on mount instead of trusting
  // the module-level default.
  const [areaOptions, setAreaOptions] = useState(config.AREA_OPTIONS || {});
  useEffect(() => {
    fetchRemoteConfig().then(() => setAreaOptions({ ...config.AREA_OPTIONS }));
  }, []);

  const selectedCategory = config.SERVICE_CONFIG?.[categoryKey];
  const selectedService  = config.SERVICE_CONFIG?.[categoryKey]?.services?.[serviceKey];
  const serviceLabel     = selectedService?.label || "Medical Care Representative";
  const categoryLabel    = selectedCategory?.label || "Care Services";

  const [formData, setFormData] = useState({
    username: "", mobilenumber: "", email: "", address: "",
    city: "", area: "", language: "", gender: "",
    urgencyStatus: "", shiftTime: "", shiftDuration: "", additionalNotes: "",
    salaryOffered: "", service_type: NURSING_OPTIONS.map((o) => o.value),
    patientCondition: "",
  });

  const availableAreas = useMemo(() => {
    const sel = areaOptions[formData.city] || areaOptions[formData.city?.toLowerCase()];
    if (Array.isArray(sel)) return sel;
    if (sel?.areas) return sel.areas;
    return [];
  }, [formData.city, areaOptions]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const cleanValue = name === "mobilenumber" ? value.replace(/\D/g, "").slice(0, 10) : value;
    setFormData((prev) => ({ ...prev, [name]: cleanValue, ...(name === "city" ? { area: "" } : {}) }));
  };

  // Step 1 -> Step 2: validate the form, then show Terms & Conditions.
  // The actual POST only happens from submitRequirement(), reached via the
  // Terms step's checkbox + Submit button — see TermsAcceptStep.
  const handleNext = (e) => {
    e.preventDefault();
    setSubmitError("");
    setStep("terms");
  };

  const submitRequirement = async () => {
    setSubmitError("");
    const detailedServiceList = NURSING_OPTIONS
      .filter((item) => formData.service_type.includes(item.value))
      .map((item) => ({ type: "home-nursing", key: item.value, value: item.value, label: item.label }));

    const requestBody = {
      username:     formData.username,
      mobilenumber: formData.mobilenumber,
      email:        formData.email,
      address:      formData.address,
      city:         formData.city,
      area:         formData.area,
      language:     formData.language,
      gender:       formData.gender,
      urgencyStatus:formData.urgencyStatus,
      shiftTime:    formData.shiftTime,
      shiftDuration:formData.shiftDuration,
      patientCondition: formData.patientCondition,
      additionalNotes: formData.additionalNotes,
      salaryOffered:formData.salaryOffered,
      service_category: categoryKey,
      selected_city: getUserCity(),
      service_type:  "home-nursing-services",
      service_list:  formData.service_type,
      detailed_service_list: detailedServiceList,
      service:       serviceKey,
      category_label: categoryLabel,
      service_label:  serviceLabel,
      terms_accepted: true,
    };

    try {
      setIsSubmitting(true);
      const res = await fetch(`${BASE_URL}/api/requirements/home-nursing/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
      const data = await res.json();

      if (res.ok && data?.success !== false) {
        setShowThankYou(true);
        setStep("form");
        setFormData({ username:"", mobilenumber:"", email:"", address:"", city:"", area:"", language:"", gender:"", urgencyStatus:"", shiftTime:"", shiftDuration:"", additionalNotes:"", salaryOffered:"", service_type: NURSING_OPTIONS.map((o) => o.value), patientCondition:"" });
      } else {
        setSubmitError(data?.message || data?.error || "Failed to submit requirement.");
      }
    } catch (error) {
      console.error("Submit error:", error);
      setSubmitError("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const styles = {
    page:        { minHeight:"100vh", background:"linear-gradient(135deg,#fff1f2 0%,#fce7f3 50%,#fdf2f8 100%)", padding:"30px 20px", fontFamily:"Segoe UI,Tahoma,Geneva,Verdana,sans-serif" },
    wrapper:     { maxWidth:"1580px", width:"96%", margin:"0 auto" },
    hero:        { background:"linear-gradient(135deg,#7f1d1d,#dc2626,#f87171)", color:"#fff", borderRadius:"28px", padding:"34px 36px", boxShadow:"0 20px 40px rgba(220,38,38,0.22)", marginBottom:"24px" },
    heroBadge:   { display:"inline-block", background:"rgba(255,255,255,0.14)", padding:"7px 14px", borderRadius:"999px", fontSize:"12px", fontWeight:"700", marginBottom:"12px" },
    heroTitle:   { margin:0, fontSize:"2.1rem", fontWeight:"800" },
    heroText:    { marginTop:"10px", marginBottom:0, color:"rgba(255,255,255,0.9)", maxWidth:"900px", lineHeight:1.6, fontSize:"15px" },
    formCard:    { background:"#fff", borderRadius:"28px", border:"1px solid #fecdd3", boxShadow:"0 14px 34px rgba(15,23,42,0.08)", overflow:"hidden", width:"100%", maxWidth:"1320px", margin:"0 auto" },
    formHeader:  { padding:"28px 40px", borderBottom:"1px solid #fecdd3", background:"linear-gradient(180deg,#ffffff,#fff1f2)" },
    formTitle:   { margin:0, fontSize:"1.6rem", fontWeight:"800", color:"#0f172a" },
    formSub:     { margin:"8px 0 0", color:"#64748b", fontSize:"14px" },
    body:        { padding:"32px 40px 36px" },
    section:     { marginBottom:"28px", padding:"24px", borderRadius:"20px", background:"#fff1f2", border:"1px solid #fecdd3" },
    sectionTitle:{ margin:"0 0 18px", fontSize:"1.1rem", fontWeight:"800", color:"#0f172a", display:"flex", alignItems:"center", gap:"8px" },
    grid:        { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:"20px", alignItems:"start" },
    field:       { display:"flex", flexDirection:"column", gap:"8px" },
    label:       { fontSize:"14px", fontWeight:"700", color:"#334155", display:"flex", alignItems:"center", gap:"8px" },
    input:       { width:"100%", padding:"14px 16px", borderRadius:"14px", border:"1px solid #d6deeb", fontSize:"15px", outline:"none", background:"#fff", boxSizing:"border-box" },
    select:      { width:"100%", padding:"14px 16px", borderRadius:"14px", border:"1px solid #d6deeb", fontSize:"15px", outline:"none", background:"#fff", boxSizing:"border-box" },
    textarea:    { width:"100%", minHeight:"120px", padding:"14px 16px", borderRadius:"14px", border:"1px solid #d6deeb", fontSize:"15px", outline:"none", background:"#fff", boxSizing:"border-box", resize:"vertical" },
    submitWrap:  { display:"flex", justifyContent:"center", marginTop:"30px" },
    submitBtn:   { border:"none", borderRadius:"999px", background:"linear-gradient(135deg,#dc2626,#ef4444)", color:"#fff", padding:"15px 34px", fontSize:"15px", fontWeight:"800", minWidth:"260px", boxShadow:"0 12px 24px rgba(220,38,38,0.24)", cursor:"pointer" },
    helper:      { color:"#64748b", fontSize:"13px", margin:0, lineHeight:1.6 },
    serviceGrid: { display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:"10px", marginTop:"10px" },
    serviceCell: { display:"flex", alignItems:"center", gap:"8px", padding:"10px 14px", borderRadius:"12px", border:"1px solid #fecdd3", background:"#fff" },
    serviceCellLabel:{ fontSize:"14px", fontWeight:"600", color:"#0f172a" },
    errorText:   { color:"#dc2626", fontSize:"14px", fontWeight:"600", marginTop:"12px" },
    popupOverlay:{ position:"fixed", inset:0, backgroundColor:"rgba(15,23,42,0.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:50 },
    popupCard:   { background:"#fff", borderRadius:"18px", padding:"22px 24px", maxWidth:"360px", width:"90%", textAlign:"center", boxShadow:"0 20px 40px rgba(15,23,42,0.25)" },
    popupTitle:  { margin:"10px 0 6px", fontSize:"1.1rem", fontWeight:"800", color:"#991b1b" },
    popupText:   { margin:0, fontSize:"14px", color:"#4b5563" },
    popupButton: { marginTop:"16px", borderRadius:"999px", border:"none", background:"#dc2626", color:"#fff", fontSize:"14px", fontWeight:"700", padding:"10px 24px", cursor:"pointer" },
    noticeBanner:{ display:"flex", gap:12, alignItems:"flex-start", background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:"16px", padding:"16px 20px", marginBottom:"20px" },
    noticeIcon:  { fontSize:22, flexShrink:0 },
    noticeTitle: { margin:"0 0 4px", fontSize:14, fontWeight:700, color:"#92400e" },
    noticeText:  { margin:0, fontSize:13, color:"#b45309", lineHeight:1.5 },
  };

  return (
    <>
      {showThankYou && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupCard}>
            <FaCheckCircle color="#dc2626" size={32}/>
            <h3 style={styles.popupTitle}>Thank you!</h3>
            <p style={styles.popupText}>Your nursing requirement has been submitted. A qualified nurse will contact you shortly.</p>
            <button type="button" style={styles.popupButton} onClick={() => { setShowThankYou(false); navigate("/"); }}>OK</button>
          </div>
        </div>
      )}

      <div style={styles.page}>
        <div style={styles.wrapper}>

          {/* Hero */}
          <div style={styles.hero}>
            <div style={styles.heroBadge}>💉 {serviceLabel}</div>
            <h1 style={styles.heroTitle}>Medical Representative / Medical Care Representative</h1>
            <p style={styles.heroText}>
              Trained home nurses for vitals monitoring, wound care, IV monitoring and more.
              Post your requirement below.
            </p>
          </div>

          {/* Disclaimer */}
          <div style={styles.noticeBanner}>
            <span style={styles.noticeIcon}>⚠️</span>
            <div>
              <p style={styles.noticeTitle}>Important Notice</p>
              <p style={styles.noticeText}>
                Home nursing support does not replace a hospital or clinical nurse.
                For ICU-level care, surgical procedures or new IV insertions, please contact your hospital directly.
              </p>
            </div>
          </div>

          {step === "form" && (
          <form style={styles.formCard} onSubmit={handleNext}>
              <div style={styles.formHeader}>
                <h2 style={styles.formTitle}>Home Nursing Requirement Form</h2>
                <p style={styles.formSub}>Fill in patient details, review the included nursing services, and mention your salary offer.</p>
              </div>

              <div style={styles.body}>
                {/* Personal Details */}
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}><FaUser color="#dc2626"/> Personal Details</h3>
                  <div style={styles.grid}>
                    {[
                      { id:"username",     label:"Contact Name",   icon:<FaUser/>,         type:"text",  required:true },
                      { id:"mobilenumber", label:"Mobile Number",   icon:<FaPhoneAlt/>,     type:"tel",   required:true, pattern:"[0-9]{10}", minLength:10, maxLength:10 },
                      { id:"email",        label:"Email",           icon:<FaEnvelope/>,     type:"email", required:true },
                      { id:"address",      label:"Address",         icon:<FaMapMarkerAlt/>, type:"text",  required:true },
                    ].map((f) => (
                      <div key={f.id} style={styles.field}>
                        <label htmlFor={f.id} style={styles.label}>{f.icon} {f.label}</label>
                        <input id={f.id} style={styles.input} type={f.type} name={f.id}
                          value={formData[f.id]} onChange={handleInputChange}
                          required={f.required} pattern={f.pattern}
                          minLength={f.minLength} maxLength={f.maxLength}
                        />
                      </div>
                    ))}

                    {/* City */}
                    <div style={styles.field}>
                      <label htmlFor="city" style={styles.label}><FaMapMarkerAlt/> City</label>
                      <select id="city" style={styles.select} name="city" value={formData.city} onChange={handleInputChange} required>
                        <option value="">Select City</option>
                        {Object.keys(areaOptions).map((c) => (
                          <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                        ))}
                      </select>
                    </div>

                    {/* Area */}
                    <div style={styles.field}>
                      <label htmlFor="area" style={styles.label}><FaMapMarkerAlt/> Area</label>
                      <select id="area" style={styles.select} name="area" value={formData.area} onChange={handleInputChange} required disabled={!formData.city}>
                        <option value="">Select Area</option>
                        {availableAreas.map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </div>

                    {/* Language */}
                    <div style={styles.field}>
                      <label htmlFor="language" style={styles.label}><FaLanguage/> Language</label>
                      <select id="language" style={styles.select} name="language" value={formData.language} onChange={handleInputChange} required>
                        <option value="">Select Language</option>
                        {["Tamil","Telugu","Kannada","Malayalam","Hindi","English"].map((l) => (
                          <option key={l} value={l.toLowerCase()}>{l}</option>
                        ))}
                      </select>
                    </div>

                    {/* Preferred Gender */}
                    <div style={styles.field}>
                      <label htmlFor="gender" style={styles.label}><FaUserTag/> Preferred Gender</label>
                      <select id="gender" style={styles.select} name="gender" value={formData.gender} onChange={handleInputChange} required>
                        <option value="">Select Gender</option>
                        <option value="male">Male Nurse</option>
                        <option value="female">Female Nurse</option>
                        <option value="any">Any</option>
                      </select>
                    </div>

                    {/* Patient Condition */}
                    <div style={{ ...styles.field, gridColumn:"1 / -1" }}>
                      <label htmlFor="patientCondition" style={styles.label}><FaStethoscope color="#dc2626"/> Patient Condition / Diagnosis</label>
                      <input id="patientCondition" style={styles.input} type="text" name="patientCondition"
                        value={formData.patientCondition} onChange={handleInputChange}
                        placeholder="e.g. Post-surgery recovery, Diabetes, Stroke patient…"
                      />
                    </div>
                  </div>
                </div>

                {/* Nursing Services */}
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}><FaStethoscope color="#dc2626"/> Nursing Services Included</h3>
                  <p style={styles.helper}>Every service below is included for this booking.</p>
                  <div style={styles.serviceGrid}>
                    {NURSING_OPTIONS.map((opt) => (
                      <div key={opt.value} style={styles.serviceCell}>
                        <FaCheckCircle color="#dc2626" size={14} />
                        <span style={styles.serviceCellLabel}>{opt.label}</span>
                      </div>
                    ))}
                  </div>
                  {submitError && <p style={styles.errorText}>{submitError}</p>}
                </div>

                {/* Timing & Salary */}
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}><FaClock color="#dc2626"/> Timing & Salary</h3>
                  <div style={styles.grid}>
                    <div style={styles.field}>
                      <label htmlFor="urgencyStatus" style={styles.label}><FaInfoCircle/> Urgency</label>
                      <select id="urgencyStatus" style={styles.select} name="urgencyStatus" value={formData.urgencyStatus} onChange={handleInputChange} required>
                        <option value="">Select</option>
                        <option value="within_days">Within Days</option>
                        <option value="within_a_week">Within a Week</option>
                        <option value="just_enquiry">Just Enquiry</option>
                      </select>
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="shiftDuration" style={styles.label}><FaClock/> Hours</label>
                      <select id="shiftDuration" style={styles.select} name="shiftDuration" value={formData.shiftDuration} onChange={handleInputChange} required>
                        <option value="">Select</option>
                        <option value="2hrs">2 Hours</option>
                        <option value="4hrs">4 Hours</option>
                        <option value="6hrs">6 Hours</option>
                        <option value="12hrs">12 Hours</option>
                        <option value="live_24hrs">Live-in (24 Hours)</option>
                      </select>
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="shiftTime" style={styles.label}><FaClock/> Shift</label>
                      <select id="shiftTime" style={styles.select} name="shiftTime" value={formData.shiftTime} onChange={handleInputChange} required>
                        <option value="">Select</option>
                        <option value="morning">Morning</option>
                        <option value="afternoon">Afternoon</option>
                        <option value="evening">Evening</option>
                        <option value="full_day">Full Day</option>
                      </select>
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="salaryOffered" style={styles.label}><FaMoneyBillWave/> Salary Offered (₹)</label>
                      <input id="salaryOffered" style={styles.input} type="number" name="salaryOffered"
                        value={formData.salaryOffered} onChange={handleInputChange} placeholder="e.g. 25000" required
                      />
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="additionalNotes" style={styles.label}><FaInfoCircle/> Additional Notes</label>
                      <textarea id="additionalNotes" style={styles.textarea} name="additionalNotes"
                        value={formData.additionalNotes} onChange={handleInputChange}
                        placeholder="Equipment available at home, special instructions, preferred timings…"
                      />
                    </div>
                  </div>
                </div>

                <div style={styles.submitWrap}>
                  <button type="submit" style={styles.submitBtn} disabled={isSubmitting}>
                    Next
                  </button>
                </div>
              </div>
            </form>
          )}

          {step === "terms" && (
            <div style={styles.formCard}>
              <TermsAcceptStep
                onBack={() => setStep("form")}
                onSubmit={submitRequirement}
                isSubmitting={isSubmitting}
                submitError={submitError}
                serviceLabel={serviceLabel}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default HomeNursingSupport;
